import { FileText, Play } from 'lucide-react';
import type { MessageSource } from '../api/conversation';
import { useMe } from '../auth/MeContext';
import { useSourcePanel } from './SourcePanelContext';
import { VideoCitationTile } from './VideoCitationTile';
import { useT } from '../I18nContext';

/**
 * Citations attached to an assistant message.
 *
 * Two display modes, switched by the tenant's
 * `display_video_inline` setting (AI Settings → Behaviour):
 *
 *   1. **Pill mode (default).** Document AND video citations render
 *      as compact pill buttons in a single row beneath the assistant
 *      message. A doc pill opens the SourcePanel slide-in. A video
 *      pill expands an inline `VideoCitationTile` above the pill row
 *      and pauses any other open source - only one source open at a
 *      time across the entire chat (shared SourcePanelContext).
 *
 *   2. **Inline mode (legacy).** When the tenant has flipped the
 *      "Show video player inline by default" toggle, video citations
 *      render as the standalone `VideoCitationTile` they had before
 *      this PR - always-visible thumbnail tile with a self-managed
 *      expand toggle. Doc citations still render as chips.
 *
 * Sources without an explicit `type` are treated as 'document' so
 * conversations persisted before the citation builder added the
 * field keep rendering correctly.
 */
interface MessageSourcesProps {
  sources: MessageSource[];
}

export function MessageSources({ sources }: MessageSourcesProps) {
  const { me } = useMe();
  const { target, openDoc, openVideo, close } = useSourcePanel();
  const t = useT();

  if (!sources.length) return null;

  // Split. Anything without a type defaults to document for backward
  // compatibility with conversations persisted before the citation
  // builder added the field.
  const docs   = sources.filter((s) => (s.type || 'document') === 'document');
  const videos = sources.filter((s) => s.type === 'video');

  const visibleDocs = docs.slice(0, 4);
  const citationViewEnabled = !!me?.citation_view_enabled;
  const inlineVideos = !!me?.display_video_inline;

  // Which (if any) of THIS message's videos is the globally-open one?
  // Drives the inline-expanded tile above the pill row in pill mode.
  const activeVideoId = target?.kind === 'video' ? target.videoId : null;
  const expandedVideoForThisMessage = !inlineVideos && activeVideoId
    ? videos.find((v) => (v.video_id || v.id) === activeVideoId) ?? null
    : null;

  return (
    <div className="message-sources-wrap">
      {/* When a video is open in pill mode, render the player tile
          above the pill row so the user sees the source they just
          clicked right next to the assistant's answer. */}
      {expandedVideoForThisMessage && (
        <div className="message-video-citations">
          <VideoCitationTile
            source={expandedVideoForThisMessage}
            expanded
            onCollapse={close}
          />
        </div>
      )}

      {(visibleDocs.length > 0 || (!inlineVideos && videos.length > 0)) && (
        <div className="message-sources">
          <FileText size={12} className="message-sources-icon" aria-hidden />
          <span className="message-sources-label">{t('message.sources_label')}</span>

          {/* Doc pills */}
          {visibleDocs.map((s, i) => {
            const title = s.title || t('message.source_doc_default');
            return (
              <SourcePill
                key={`d:${s.id || i}`}
                title={title}
                icon={<FileText size={12} />}
                onClick={
                  citationViewEnabled && s.id
                    ? () => openDoc(s.id, title)
                    : null
                }
                ariaLabel={t('message.source_view_doc_aria', { title })}
              />
            );
          })}

          {/* Video pills (pill mode only) */}
          {!inlineVideos && videos.map((s, i) => {
            const title = s.title || t('message.source_video_default');
            const vid = s.video_id || s.id || `v:${i}`;
            const stamp = s.start_time_display || '';
            const isActive = activeVideoId === vid;
            const label = stamp ? `${title} · ${stamp}` : title;
            return (
              <SourcePill
                key={`v:${vid}`}
                title={label}
                icon={<Play size={12} />}
                onClick={() =>
                  isActive ? close() : openVideo(vid)
                }
                ariaLabel={isActive
                  ? t('message.source_hide_video_aria', { title })
                  : stamp
                    ? t('message.source_play_video_at_aria', { title, stamp })
                    : t('message.source_play_video_aria', { title })}
                isActive={isActive}
              />
            );
          })}
        </div>
      )}

      {/* Inline mode (legacy): always-visible video tile per video. */}
      {inlineVideos && videos.length > 0 && (
        <div className="message-video-citations">
          {videos.map((s, i) => (
            <VideoCitationTile key={s.video_id || s.id || i} source={s} />
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Shared pill renderer for doc + video citations.
// ─────────────────────────────────────────────────────────────

interface SourcePillProps {
  title: string;
  icon: React.ReactNode;
  /** When null, the pill renders as a non-interactive chip (used for
   *  tenants that haven't enabled the citation-view feature - they
   *  see WHICH sources matched but can't drill in). */
  onClick: (() => void) | null;
  ariaLabel: string;
  isActive?: boolean;
}

function SourcePill({ title, icon, onClick, ariaLabel, isActive }: SourcePillProps) {
  const cls = 'message-sources-pill' + (isActive ? ' is-active' : '');
  if (!onClick) {
    return (
      <span className={cls + ' is-inert'}>
        {icon}
        <span>{title}</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      title={ariaLabel}
      aria-pressed={isActive ? true : undefined}
    >
      {icon}
      <span>{title}</span>
    </button>
  );
}
